<!-- The app's ONE navigation bar. Factory-owned.

     THE PROBLEM THIS SOLVES. Nothing in the chassis used to style or shape a
     toolbar, so every screen answered the question itself: one app came out of
     a single build with six toolbar patterns across twenty-four screens — some
     with a back button and some without, two printing their own name twice, one
     using its title as a live status field, one with no bar at all. Every
     individual screen looked fine. The defect only exists in the aggregate, and
     nothing in the pipeline ever looks at twenty-four screens at once.

     THE CONTRACT — two tiers, and every screen is one of them:

       a tab root        this bar, plus <AppLargeTitle> inside the content
       a pushed screen   this bar alone — back button, the screen's name, and
                         AT MOST ONE trailing action

     THE TITLE IS THE THING, NEVER THE STATE. A bar bound to booking status put
     "On the way" where the screen's name should be: the screen had no name, the
     name changed under the customer while they read it, and the back stack's
     label changed with it. The bar says what you are looking at; the content
     says what is happening to it.

     A SCREEN PRINTS ITS NAME ONCE. A tab root with a large title does not also
     carry an <h1>. A pushed screen carries a content heading only when it says
     something the bar does not.

     THE HAIRLINE IS EARNED. iOS draws a separator under a bar only once content
     has scrolled beneath it, and Ionic manages that itself for the iOS condense
     case and nowhere else. This listens to the page's own scroll element, so
     the rule is the same on a pushed screen as on a tab root, and the same on
     Android as on iOS. -->
<template>
  <IonHeader
    ref="host"
    class="app-header"
    :class="{ 'app-header--scrolled': scrolled }"
    :translucent="true"
  >
    <IonToolbar>
      <!-- Back OR the menu button, never both: they occupy the same corner and
           mean opposite things. A pushed screen goes back; a sidebar
           destination opens the menu. Ionic hides `IonMenuButton` by itself
           once the split pane is showing, so it is correct at every width. -->
      <!-- A DESTINATION, NOT A HISTORY POP.
           `IonBackButton`'s `default-href` is a FALLBACK: it pops the stack
           when there is something to pop and only falls back when there is
           not. A screen arrived at by `router.replace()` — which is what every
           finished run, wizard and checkout does, correctly, so the result is
           not back-navigable — has had its previous entry removed, and Ionic's
           own stack still holds one. So the chevron rendered, looked tappable,
           and did nothing. Reported from a real device; it does not reproduce
           in a headless browser.
           Every caller means "go here", so that is what this does: `back` is
           the destination, and `@click.prevent` stops the pop from running
           first. -->
      <IonButtons v-if="back" slot="start">
        <IonBackButton :default-href="back" :text="backText" @click.prevent="goBack" />
      </IonButtons>
      <IonButtons v-else-if="menu" slot="start">
        <IonMenuButton />
      </IonButtons>

      <IonTitle>{{ title }}</IonTitle>

      <!-- At most one action, and the page supplies it as
           `<IonButtons slot="end">`. The DEFAULT slot, not a named one: the
           `slot="end"` the page writes is an attribute for ION-TOOLBAR's own
           slot, not a name for this component's. Asking for `name="end"` here
           matches nothing and silently drops the action, with no error
           anywhere. Rendering it as a direct child of the toolbar is what puts
           it in the toolbar's end slot. -->
      <slot />
    </IonToolbar>
  </IonHeader>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonBackButton, IonMenuButton,
} from '@ionic/vue';

const props = defineProps({
  /** What you are looking at. A noun. Never a status, never a question. */
  title: { type: String, default: '' },
  /** Where Back goes when there is no history to pop. Omit on a tab root. */
  back: { type: String, default: '' },
  /** iOS shows a word beside the chevron; Material shows the chevron alone. */
  backText: { type: String, default: 'Back' },
  /** Sidebar shell only: a destination screen opens the menu instead of going
   *  back. Ignored when `back` is set — one corner, one meaning. */
  menu: { type: Boolean, default: false },
});

const router = useRouter();

/**
 * Go to `back`, rather than popping whatever happens to be on the stack.
 *
 * `replace`, not `push`: the screen showing this bar is usually a terminal one
 * — a results screen, a confirmation — and pushing would leave it behind the
 * destination for the NEXT back gesture to return to.
 */
const goBack = () => {
  if (props.back) router.replace(props.back);
};

const host = ref(null);
const scrolled = ref(false);

let scrollEl = null;

const onScroll = () => {
  // A threshold rather than `> 0`: iOS rubber-bands past zero on every touch,
  // and a hairline that blinks on a bounce is worse than no hairline.
  scrolled.value = (scrollEl?.scrollTop ?? 0) > 4;
};

onMounted(async () => {
  // The header is a SIBLING of the content inside `ion-page`, so the page is
  // the only reliable common ancestor. `closest()` survives a wrapper being
  // added later in a way `parentElement` does not.
  const el = host.value?.$el;
  const page = el?.closest?.('.ion-page') ?? el?.parentElement;
  const content = page?.querySelector?.('ion-content');
  if (!content?.getScrollElement) return;

  // A native scroll listener rather than Ionic's `ionScroll`, so the page does
  // not have to opt into `scroll-events` — which fires a Stencil event per
  // frame for the whole app to pay for one hairline.
  scrollEl = await content.getScrollElement();
  scrollEl.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
});

onBeforeUnmount(() => {
  scrollEl?.removeEventListener('scroll', onScroll);
  scrollEl = null;
});
</script>
